import { useState, useEffect, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import {
  Phone, MessageCircle, Wallet, Pencil, Archive, ArrowLeft, MapPin, Activity,
  CalendarClock, FileText, Stethoscope, TrendingDown, RotateCcw,
  PauseCircle, CheckCircle2, XCircle, PlayCircle, Clock, Calendar,
  Package, CreditCard, ArrowDownRight, ArrowUpRight, MinusCircle, PlusCircle, Receipt, Ban,
} from "lucide-react";
import { ResponsiveContainer, LineChart, Line, XAxis, YAxis, Tooltip, CartesianGrid } from "recharts";
import { api, apiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { inr, fmtDate, fmtDateTime, telLink, waLink, mapsLink, initials } from "@/lib/format";
import { Loading, StatusBadge, EmptyState, AccountBalanceBadge } from "@/components/common";
import TreatmentRecordDialog from "@/components/TreatmentRecordDialog";
import PaymentDialog from "@/components/PaymentDialog";
import PatientFormDialog from "@/components/PatientFormDialog";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Avatar, AvatarFallback } from "@/components/ui/avatar";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Checkbox } from "@/components/ui/checkbox";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import { toast } from "sonner";

function Row({ label, value }) {
  return (
    <div className="flex justify-between gap-4 py-2 border-b border-slate-100 last:border-0">
      <span className="text-sm text-slate-500">{label}</span>
      <span className="text-sm font-medium text-slate-800 text-right">{value ?? "-"}</span>
    </div>
  );
}

export default function PatientProfile() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [p, setP] = useState(null);
  const [ledger, setLedger] = useState([]);
  const [patientLedger, setPatientLedger] = useState([]);
  const [packagesList, setPackagesList] = useState([]);
  const [paymentsList, setPaymentsList] = useState([]);
  const [treatment, setTreatment] = useState([]);
  const [timeline, setTimeline] = useState([]);
  const [payOpen, setPayOpen] = useState(false);
  const [editOpen, setEditOpen] = useState(false);

  // Financial Modals
  const [refundOpen, setRefundOpen] = useState(false);
  const [refundAmount, setRefundAmount] = useState("");
  const [refundMethod, setRefundMethod] = useState("cash");
  const [refundReason, setRefundReason] = useState("");
  const [refundNote, setRefundNote] = useState("");
  const [refundLoading, setRefundLoading] = useState(false);

  const [adjOpen, setAdjOpen] = useState(false);
  const [adjDir, setAdjDir] = useState("credit");
  const [adjAmount, setAdjAmount] = useState("");
  const [adjReason, setAdjReason] = useState("");
  const [adjNote, setAdjNote] = useState("");
  const [adjLoading, setAdjLoading] = useState(false);

  const [voidOpen, setVoidOpen] = useState(false);
  const [selectedForVoid, setSelectedForVoid] = useState(null);
  const [voidReason, setVoidReason] = useState("");
  const [voidLoading, setVoidLoading] = useState(false);

  // Lifecycle Dialogs
  const [pauseOpen, setPauseOpen] = useState(false);
  const [pauseUntil, setPauseUntil] = useState("");
  const [pauseReason, setPauseReason] = useState("");
  const [pauseNextVisit, setPauseNextVisit] = useState("");
  const [pauseNotes, setPauseNotes] = useState("");

  const [completeOpen, setCompleteOpen] = useState(false);
  const [completeDate, setCompleteDate] = useState("");
  const [completeNote, setCompleteNote] = useState("");
  const [completeWa, setCompleteWa] = useState(false);

  const [discOpen, setDiscOpen] = useState(false);
  const [discDate, setDiscDate] = useState("");
  const [discReason, setDiscReason] = useState("");
  const [discNote, setDiscNote] = useState("");

  const [reactivateOpen, setReactivateOpen] = useState(false);
  const [reactivateDate, setReactivateDate] = useState("");
  const [reactivateNextVisit, setReactivateNextVisit] = useState("");
  const [reactivateSchedule, setReactivateSchedule] = useState("daily");
  const [reactivateNote, setReactivateNote] = useState("");

  const [treatmentRecordOpen, setTreatmentRecordOpen] = useState(false);
  const [selectedTreatment, setSelectedTreatment] = useState(null);

  const [actionLoading, setActionLoading] = useState(false);

  const load = useCallback(async () => {
    try {
      const { data } = await api.get(`/patients/${id}`);
      setP(data);
    } catch (e) {
      toast.error("Patient not found");
      navigate("/patients");
    }
  }, [id, navigate]);

  const loadExtras = useCallback(async () => {
    const [lg, pl, pkg, pays, tr, tl] = await Promise.all([
      api.get("/advance-ledger", { params: { patient_id: id } }).catch(() => ({ data: { items: [] } })),
      api.get("/patient-ledger", { params: { patient_id: id } }).catch(() => ({ data: { items: [] } })),
      api.get("/packages", { params: { patient_id: id } }).catch(() => ({ data: { items: [] } })),
      api.get("/payments", { params: { patient_id: id } }).catch(() => ({ data: { items: [] } })),
      api.get("/treatment-sessions", { params: { patient_id: id } }).catch(() => ({ data: { items: [] } })),
      api.get(`/patients/${id}/timeline`).catch(() => ({ data: { events: [] } })),
    ]);
    setLedger(lg.data.items || []);
    setPatientLedger(pl.data.items || []);
    setPackagesList(pkg.data.items || []);
    setPaymentsList(pays.data.items || []);
    setTreatment(tr.data.items || []);
    setTimeline(tl.data.events || []);
  }, [id]);

  useEffect(() => { load(); }, [load]);
  useEffect(() => { if (p) loadExtras(); }, [p?.id]); // eslint-disable-line

  const handleRefund = async () => {
    if (!refundAmount || Number(refundAmount) <= 0) {
      toast.error("Enter a valid refund amount");
      return;
    }
    if (!refundReason || refundReason.trim().length < 3) {
      toast.error("Refund reason is mandatory (minimum 3 characters)");
      return;
    }
    setRefundLoading(true);
    try {
      await api.post("/payments/refund", {
        patient_id: id,
        amount: Number(refundAmount),
        method: refundMethod,
        reason: refundReason.trim(),
        notes: refundNote,
      });
      toast.success("Refund processed successfully");
      setRefundOpen(false);
      load();
      loadExtras();
    } catch (e) {
      toast.error(apiError(e));
    } finally {
      setRefundLoading(false);
    }
  };

  const handleManualAdj = async () => {
    if (!adjAmount || Number(adjAmount) <= 0) {
      toast.error("Enter a valid adjustment amount");
      return;
    }
    if (!adjReason || adjReason.trim().length < 3) {
      toast.error("Adjustment reason is mandatory (minimum 3 characters)");
      return;
    }
    setAdjLoading(true);
    try {
      await api.post("/payments/manual-adjustment", {
        patient_id: id,
        direction: adjDir,
        amount: Number(adjAmount),
        reason: adjReason.trim(),
        notes: adjNote,
      });
      toast.success(`Manual ${adjDir.toUpperCase()} adjustment recorded`);
      setAdjOpen(false);
      load();
      loadExtras();
    } catch (e) {
      toast.error(apiError(e));
    } finally {
      setAdjLoading(false);
    }
  };

  const handleVoidPayment = async () => {
    if (!voidReason || voidReason.trim().length < 3) {
      toast.error("Void reason is mandatory (minimum 3 characters)");
      return;
    }
    setVoidLoading(true);
    try {
      await api.post(`/payments/${selectedForVoid.id}/void`, {
        reason: voidReason.trim(),
      });
      toast.success("Payment voided successfully");
      setVoidOpen(false);
      load();
      loadExtras();
    } catch (e) {
      toast.error(apiError(e));
    } finally {
      setVoidLoading(false);
    }
  };

  const archive = async () => {
    try { await api.post(`/patients/${id}/archive`); toast.success("Patient archived"); load(); }
    catch (e) { toast.error(apiError(e)); }
  };

  // Pause submit
  const handlePause = async () => {
    if (!pauseUntil) {
      toast.error("Please select a Pause Until Date");
      return;
    }
    setActionLoading(true);
    try {
      await api.post(`/patients/${id}/pause`, {
        pause_until_date: pauseUntil,
        pause_reason: pauseReason,
        next_visit_date: pauseNextVisit || null,
        notes: pauseNotes,
      });
      toast.success("Treatment paused successfully");
      setPauseOpen(false);
      load();
    } catch (e) {
      toast.error(apiError(e));
    } finally {
      setActionLoading(false);
    }
  };

  // Complete submit
  const handleComplete = async () => {
    setActionLoading(true);
    try {
      await api.post(`/patients/${id}/complete`, {
        completion_date: completeDate || null,
        note: completeNote,
        send_whatsapp: completeWa,
      });
      toast.success("Treatment marked as Completed");
      setCompleteOpen(false);
      load();
    } catch (e) {
      toast.error(apiError(e));
    } finally {
      setActionLoading(false);
    }
  };

  // Discontinue submit
  const handleDiscontinue = async () => {
    setActionLoading(true);
    try {
      await api.post(`/patients/${id}/discontinue`, {
        discontinuation_date: discDate || null,
        reason: discReason,
        note: discNote,
      });
      toast.success("Treatment discontinued");
      setDiscOpen(false);
      load();
    } catch (e) {
      toast.error(apiError(e));
    } finally {
      setActionLoading(false);
    }
  };

  // Reactivate submit
  const handleReactivate = async () => {
    setActionLoading(true);
    try {
      await api.post(`/patients/${id}/reactivate`, {
        reactivation_date: reactivateDate || null,
        next_visit_date: reactivateNextVisit || null,
        schedule: reactivateSchedule || null,
        note: reactivateNote,
      });
      toast.success("Treatment reactivated successfully");
      setReactivateOpen(false);
      load();
    } catch (e) {
      toast.error(apiError(e));
    } finally {
      setActionLoading(false);
    }
  };

  if (!p) return <Loading />;
  const st = p.stats || {};
  const painData = treatment.slice().reverse().filter((t) => t.pain_score != null).map((t, i) => ({ n: `S${t.session_number || i + 1}`, pain: t.pain_score }));
  const isHome = p.service_type === "home" || p.service_type === "both";
  const treatmentStatus = p.treatment_status || (p.status === "archived" ? "archived" : "active");
  const canManageTreatment = user?.role === "admin" || user?.role === "receptionist";
  const canCompletePhysio = user?.role === "physiotherapist" && p.assigned_physio_id === user?.id;

  return (
    <div>
      <button onClick={() => navigate(-1)} className="flex items-center gap-1 text-sm text-slate-500 hover:text-slate-700 mb-3"><ArrowLeft className="h-4 w-4" /> Back</button>

      {/* Header Card */}
      <Card className="p-5 mb-5" data-testid="patient-profile-header">
        <div className="flex flex-col sm:flex-row sm:items-center gap-4">
          <Avatar className="h-16 w-16">
            <AvatarFallback className="bg-bk-lightGreen text-bk-green text-xl font-bold">{initials(p.name)}</AvatarFallback>
          </Avatar>
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2 flex-wrap">
              <h1 className="font-display font-extrabold text-2xl text-bk-blue">{p.name}</h1>
              <Badge variant="outline" className="border-slate-200 text-slate-500">{p.patient_id}</Badge>
              <StatusBadge status={treatmentStatus} />
              {p.status === "archived" && <Badge variant="secondary">Archived</Badge>}
            </div>
            <p className="text-sm text-slate-500 mt-0.5">
              {p.problem} · {p.age ? `${p.age}y` : ""} {p.gender || ""} · <span className="capitalize">{p.service_type}</span>
            </p>
          </div>
          <div className="flex gap-2 flex-wrap">
            <a href={telLink(p.phone)}><Button variant="outline" size="sm" className="text-bk-blue" data-testid="profile-call"><Phone className="h-4 w-4" /></Button></a>
            <a href={waLink(p.whatsapp || p.phone)} target="_blank" rel="noreferrer"><Button variant="outline" size="sm" className="text-bk-green" data-testid="profile-wa"><MessageCircle className="h-4 w-4" /></Button></a>
            {isHome && <a href={mapsLink(p)} target="_blank" rel="noreferrer"><Button variant="outline" size="sm" className="text-bk-info"><MapPin className="h-4 w-4" /></Button></a>}
            {user?.role !== "physiotherapist" && (
              <>
                <Button size="sm" onClick={() => setPayOpen(true)} data-testid="profile-add-payment" className="bg-bk-green hover:bg-bk-greenDark"><Wallet className="h-4 w-4 mr-1" /> Payment</Button>
                <Button variant="outline" size="sm" onClick={() => setEditOpen(true)} data-testid="profile-edit"><Pencil className="h-4 w-4" /></Button>
              </>
            )}
            {user?.role === "admin" && p.status === "active" && (
              <AlertDialog>
                <AlertDialogTrigger asChild><Button variant="outline" size="sm" className="text-bk-danger" data-testid="profile-archive"><Archive className="h-4 w-4" /></Button></AlertDialogTrigger>
                <AlertDialogContent>
                  <AlertDialogHeader><AlertDialogTitle>Archive patient?</AlertDialogTitle><AlertDialogDescription>The patient will be hidden from operational lists. History is preserved.</AlertDialogDescription></AlertDialogHeader>
                  <AlertDialogFooter><AlertDialogCancel>Cancel</AlertDialogCancel><AlertDialogAction onClick={archive} className="bg-bk-danger hover:bg-red-700">Archive</AlertDialogAction></AlertDialogFooter>
                </AlertDialogContent>
              </AlertDialog>
            )}
            {user?.role === "admin" && p.status === "archived" && (
              <Button variant="outline" size="sm" onClick={() => { setReactivateSchedule(p.schedule || "daily"); setReactivateOpen(true); }} className="text-bk-green"><RotateCcw className="h-4 w-4 mr-1" /> Reactivate</Button>
            )}
          </div>
        </div>
      </Card>

      {/* Treatment Status & Actions Banner */}
      <Card className="p-4 mb-3 border-l-4 border-l-bk-green bg-slate-50/50">
        <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4">
          <div className="space-y-1">
            <div className="flex items-center gap-2 flex-wrap">
              <span className="text-xs font-semibold uppercase tracking-wider text-slate-500">Treatment Status:</span>
              <StatusBadge status={treatmentStatus} />
              {p.schedule && (
                <Badge variant="outline" className="text-xs bg-white text-slate-600 border-slate-200">
                  <Clock className="h-3 w-3 mr-1" />
                  Schedule: {p.schedule.replace(/_/g, " ").toUpperCase()}
                </Badge>
              )}
              {p.next_visit_date && (
                <Badge variant="outline" className="text-xs bg-indigo-50 text-indigo-700 border-indigo-200">
                  <Calendar className="h-3 w-3 mr-1" />
                  Next Visit: {fmtDate(p.next_visit_date)} {p.next_visit_time || ""}
                </Badge>
              )}
            </div>

            {/* Context details */}
            {treatmentStatus === "paused" && (
              <p className="text-sm text-amber-800 font-medium">
                ⏸️ Paused until <strong>{fmtDate(p.pause_until_date)}</strong> {p.pause_reason ? `· Reason: ${p.pause_reason}` : ""}
              </p>
            )}
            {treatmentStatus === "completed" && (
              <p className="text-sm text-blue-800 font-medium">
                ✅ Treatment completed on <strong>{fmtDate(p.treatment_completed_at)}</strong> {p.treatment_completion_note ? `· ${p.treatment_completion_note}` : ""}
              </p>
            )}
            {treatmentStatus === "discontinued" && (
              <p className="text-sm text-rose-800 font-medium">
                🛑 Discontinued on <strong>{fmtDate(p.discontinued_at)}</strong> {p.discontinuation_reason ? `· Reason: ${p.discontinuation_reason}` : ""}
              </p>
            )}
          </div>

          {/* Action buttons */}
          <div className="flex items-center gap-2 flex-wrap">
            {treatmentStatus === "active" && (canManageTreatment || canCompletePhysio) && (
              <>
                {canManageTreatment && (
                  <Button
                    size="sm"
                    variant="outline"
                    className="text-amber-700 hover:bg-amber-50 hover:border-amber-300"
                    onClick={() => {
                      setPauseUntil("");
                      setPauseReason("");
                      setPauseNextVisit("");
                      setPauseNotes("");
                      setPauseOpen(true);
                    }}
                  >
                    <PauseCircle className="h-4 w-4 mr-1" /> Pause Treatment
                  </Button>
                )}
                <Button
                  size="sm"
                  variant="outline"
                  className="text-blue-700 hover:bg-blue-50 hover:border-blue-300"
                  onClick={() => {
                    setCompleteDate(new Date().toISOString().slice(0, 10));
                    setCompleteNote("");
                    setCompleteWa(false);
                    setCompleteOpen(true);
                  }}
                >
                  <CheckCircle2 className="h-4 w-4 mr-1" /> Mark Completed
                </Button>
                {canManageTreatment && (
                  <Button
                    size="sm"
                    variant="outline"
                    className="text-rose-700 hover:bg-rose-50 hover:border-rose-300"
                    onClick={() => {
                      setDiscDate(new Date().toISOString().slice(0, 10));
                      setDiscReason("");
                      setDiscNote("");
                      setDiscOpen(true);
                    }}
                  >
                    <XCircle className="h-4 w-4 mr-1" /> Discontinue
                  </Button>
                )}
              </>
            )}

            {treatmentStatus !== "active" && canManageTreatment && (
              <Button
                size="sm"
                className="bg-emerald-600 hover:bg-emerald-700 text-white"
                onClick={() => {
                  setReactivateDate(new Date().toISOString().slice(0, 10));
                  setReactivateNextVisit("");
                  setReactivateSchedule(p.schedule || "daily");
                  setReactivateNote("");
                  setReactivateOpen(true);
                }}
              >
                <PlayCircle className="h-4 w-4 mr-1" /> Reactivate Treatment
              </Button>
            )}
          </div>
        </div>
      </Card>

      {/* Account Balance & Quick Financial Actions Banner */}
      <Card className="p-4 mb-5 border-l-4 border-l-bk-blue bg-blue-50/20">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
          <div className="flex items-center gap-2.5 flex-wrap">
            <span className="text-xs font-semibold uppercase tracking-wider text-slate-500">Account Balance:</span>
            <AccountBalanceBadge balance={p.account_balance ?? p.advance_balance} className="text-sm px-3 py-1" />
            {p.session_fee != null && (
              <Badge variant="outline" className="text-xs bg-white text-slate-600 border-slate-200">
                Fee: {inr(p.session_fee)} / session
              </Badge>
            )}
            {packagesList.some((pkg) => pkg.status === "active" && pkg.sessions_remaining > 0) && (
              <Badge variant="outline" className="text-xs bg-indigo-50 text-indigo-700 border-indigo-200">
                <Package className="h-3 w-3 mr-1" />
                Active Package: {packagesList.find((pkg) => pkg.status === "active").sessions_remaining} sessions left
              </Badge>
            )}
          </div>

          <div className="flex items-center gap-2 flex-wrap">
            <Button
              size="sm"
              onClick={() => setPayOpen(true)}
              className="bg-emerald-600 hover:bg-emerald-700 text-white gap-1.5 h-8 text-xs font-semibold"
            >
              <Wallet className="h-3.5 w-3.5" /> Add Money to Account
            </Button>

            {user?.role === "admin" && (
              <>
                <Button
                  size="sm"
                  variant="outline"
                  onClick={() => {
                    setRefundAmount("");
                    setRefundReason("");
                    setRefundNote("");
                    setRefundOpen(true);
                  }}
                  className="h-8 text-xs text-rose-700 border-rose-200 hover:bg-rose-50 gap-1"
                >
                  <MinusCircle className="h-3.5 w-3.5" /> Refund
                </Button>

                <Button
                  size="sm"
                  variant="outline"
                  onClick={() => {
                    setAdjAmount("");
                    setAdjReason("");
                    setAdjNote("");
                    setAdjDir("credit");
                    setAdjOpen(true);
                  }}
                  className="h-8 text-xs text-indigo-700 border-indigo-200 hover:bg-indigo-50 gap-1"
                >
                  <PlusCircle className="h-3.5 w-3.5" /> Adjust
                </Button>
              </>
            )}
          </div>
        </div>
      </Card>

      <Tabs defaultValue="overview">
        <TabsList className="mb-4 flex-wrap h-auto">
          <TabsTrigger value="overview" data-testid="tab-overview">Overview</TabsTrigger>
          <TabsTrigger value="ledger" data-testid="tab-ledger">
            Financial Ledger ({patientLedger.length})
          </TabsTrigger>
          <TabsTrigger value="packages" data-testid="tab-packages">
            Packages ({packagesList.length})
          </TabsTrigger>
          <TabsTrigger value="payments" data-testid="tab-payments">
            Receipts ({paymentsList.length})
          </TabsTrigger>
          <TabsTrigger value="attendance" data-testid="tab-attendance">Attendance</TabsTrigger>
          <TabsTrigger value="treatment" data-testid="tab-treatment">Treatment</TabsTrigger>
          <TabsTrigger value="timeline" data-testid="tab-timeline">Timeline</TabsTrigger>
        </TabsList>

        <TabsContent value="overview">
          <div className="grid lg:grid-cols-3 gap-4">
            <Card className="p-5">
              <p className="font-display font-bold text-slate-700 mb-2 flex items-center gap-2">
                <Stethoscope className="h-4 w-4 text-bk-green" /> Basic & Medical
              </p>
              <Row label="Phone" value={p.phone} />
              <Row label="WhatsApp" value={p.whatsapp} />
              <Row label="Address" value={[p.address, p.locality, p.city].filter(Boolean).join(", ")} />
              <Row label="Chief Complaint" value={p.chief_complaint || p.problem} />
              <Row label="Pain Level" value={p.pain_level != null ? `${p.pain_level}/10` : "-"} />
              <Row label="Medical History" value={p.medical_history} />
              <Row label="Referred By" value={p.referred_by} />
              <Row label="Registered" value={fmtDate(p.registration_date)} />
            </Card>
            <Card className="p-5">
              <p className="font-display font-bold text-slate-700 mb-2 flex items-center gap-2">
                <Wallet className="h-4 w-4 text-bk-green" /> Financial Summary
              </p>
              <Row label="Account Balance" value={<AccountBalanceBadge balance={p.account_balance ?? p.advance_balance} />} />
              <Row label="Session Fee" value={inr(p.session_fee)} />
              <Row label="Total Paid" value={inr(p.total_paid)} />
              <Row label="Session Charges" value={inr(p.total_session_charges)} />
              <Row label="Total Refunds" value={inr(p.total_refunds)} />
              {p.assigned_physio_name && <Row label="Physiotherapist" value={p.assigned_physio_name} />}
            </Card>
            <Card className="p-5">
              <p className="font-display font-bold text-slate-700 mb-2 flex items-center gap-2">
                <Activity className="h-4 w-4 text-bk-green" /> Attendance Summary
              </p>
              <Row label="Total Sessions" value={st.total_sessions || 0} />
              <Row label="Present" value={st.present || 0} />
              <Row label="Absent" value={st.absent || 0} />
              <Row label="Cancelled" value={st.cancelled || 0} />
              <Row label="First Visit" value={fmtDate(st.first_visit)} />
              <Row label="Last Visit" value={fmtDate(st.last_visit)} />
            </Card>
          </div>
        </TabsContent>

        {/* --- FINANCIAL LEDGER TAB --- */}
        <TabsContent value="ledger">
          <Card className="p-5">
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 mb-4">
              <div>
                <h3 className="font-display font-bold text-slate-800 text-base">Immutable Patient Financial Ledger</h3>
                <p className="text-xs text-slate-500 mt-0.5">
                  Complete chronological audit trail of all credits, session debits, and adjustments.
                </p>
              </div>
              <Button
                size="sm"
                onClick={() => setPayOpen(true)}
                className="bg-emerald-600 hover:bg-emerald-700 text-white gap-1.5 h-8 text-xs font-semibold shrink-0"
              >
                <Wallet className="h-3.5 w-3.5" /> Add Money
              </Button>
            </div>

            {patientLedger.length === 0 ? (
              <EmptyState icon={Receipt} title="No financial ledger entries yet" subtitle="Payments, session charges, and adjustments will appear here." />
            ) : (
              <div className="overflow-x-auto border rounded-lg">
                <table className="w-full text-xs text-left">
                  <thead className="bg-slate-50 text-slate-600 uppercase tracking-wider font-semibold border-b">
                    <tr>
                      <th className="py-2.5 px-3">Date & Time</th>
                      <th className="py-2.5 px-3">Entry Type</th>
                      <th className="py-2.5 px-3">Notes & Reference</th>
                      <th className="py-2.5 px-3 text-right">Credit (+)</th>
                      <th className="py-2.5 px-3 text-right">Debit (-)</th>
                      <th className="py-2.5 px-3 text-right">Running Balance</th>
                      <th className="py-2.5 px-3">Created By</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-slate-100">
                    {patientLedger.map((row) => {
                      const isCredit = row.direction === "credit";
                      const amt = Number(row.amount || 0);
                      const bal = Number(row.balance_after || 0);

                      return (
                        <tr key={row.id} className="hover:bg-slate-50/60 transition-colors">
                          <td className="py-2 px-3 text-slate-500 whitespace-nowrap font-mono">
                            {fmtDateTime(row.date)}
                          </td>
                          <td className="py-2 px-3 whitespace-nowrap">
                            <Badge
                              variant="outline"
                              className={`capitalize font-semibold text-[10px] ${
                                row.type === "money_received"
                                  ? "bg-emerald-50 text-emerald-700 border-emerald-200"
                                  : row.type === "session_charge"
                                  ? "bg-amber-50 text-amber-700 border-amber-200"
                                  : row.type === "package_created"
                                  ? "bg-indigo-50 text-indigo-700 border-indigo-200"
                                  : row.type === "package_session_utilization"
                                  ? "bg-purple-50 text-purple-700 border-purple-200"
                                  : row.type === "refund" || row.type === "void"
                                  ? "bg-rose-50 text-rose-700 border-rose-200"
                                  : "bg-slate-50 text-slate-700"
                              }`}
                            >
                              {row.type?.replace(/_/g, " ")}
                            </Badge>
                          </td>
                          <td className="py-2 px-3 text-slate-700 max-w-xs">
                            <span className="line-clamp-2">{row.notes || "-"}</span>
                            {row.receipt_no && (
                              <span className="text-[10px] font-mono text-indigo-600 block mt-0.5">
                                {row.receipt_no}
                              </span>
                            )}
                          </td>
                          <td className="py-2 px-3 text-right tabular-nums font-semibold text-emerald-600">
                            {isCredit ? inr(amt) : "-"}
                          </td>
                          <td className="py-2 px-3 text-right tabular-nums font-semibold text-rose-600">
                            {!isCredit ? inr(amt) : "-"}
                          </td>
                          <td className="py-2 px-3 text-right tabular-nums font-bold">
                            <span className={bal > 0 ? "text-emerald-700" : (bal < 0 ? "text-rose-700" : "text-slate-500")}>
                              {bal > 0 ? `Credit ${inr(bal)}` : (bal < 0 ? `Due ${inr(Math.abs(bal))}` : "Settled")}
                            </span>
                          </td>
                          <td className="py-2 px-3 text-slate-400 text-[11px] whitespace-nowrap">
                            {row.created_by_name || row.created_by || "System"}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}
          </Card>
        </TabsContent>

        {/* --- CUSTOM PACKAGES TAB --- */}
        <TabsContent value="packages">
          <Card className="p-5">
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 mb-4">
              <div>
                <h3 className="font-display font-bold text-slate-800 text-base">Custom Treatment Packages</h3>
                <p className="text-xs text-slate-500 mt-0.5">
                  Agreed multi-session packages with allocated per-session utilization.
                </p>
              </div>
              <Button
                size="sm"
                onClick={() => setPayOpen(true)}
                className="bg-indigo-600 hover:bg-indigo-700 text-white gap-1.5 h-8 text-xs font-semibold shrink-0"
              >
                <Package className="h-3.5 w-3.5" /> Create Package
              </Button>
            </div>

            {packagesList.length === 0 ? (
              <EmptyState icon={Package} title="No custom packages created" subtitle="Pre-agreed packages with session tracking will appear here." />
            ) : (
              <div className="grid gap-3 sm:grid-cols-2">
                {packagesList.map((pkg) => {
                  const used = pkg.sessions_used || 0;
                  const total = pkg.sessions_total || 1;
                  const pct = Math.min(100, Math.round((used / total) * 100));

                  return (
                    <Card key={pkg.id} className="p-4 border-slate-200 hover:shadow-sm">
                      <div className="flex items-start justify-between gap-2 mb-2">
                        <div>
                          <p className="font-bold text-slate-900 text-sm">{pkg.name}</p>
                          <p className="text-xs text-slate-500 mt-0.5">
                            Total: <strong className="text-slate-800">{inr(pkg.total_amount)}</strong> · Rate: {inr(pkg.session_rate)}/session
                          </p>
                        </div>
                        <Badge
                          variant="outline"
                          className={`capitalize font-semibold text-[10px] ${
                            pkg.status === "active"
                              ? "bg-emerald-50 text-emerald-700 border-emerald-200"
                              : "bg-slate-100 text-slate-500"
                          }`}
                        >
                          {pkg.status}
                        </Badge>
                      </div>

                      {/* Progress bar */}
                      <div className="space-y-1.5 my-3">
                        <div className="flex justify-between text-xs font-medium text-slate-700">
                          <span>{used} of {total} sessions used</span>
                          <span className="font-bold text-indigo-700">{pkg.sessions_remaining} remaining</span>
                        </div>
                        <div className="w-full h-2.5 bg-slate-100 rounded-full overflow-hidden">
                          <div
                            className="h-full bg-indigo-600 transition-all rounded-full"
                            style={{ width: `${pct}%` }}
                          />
                        </div>
                      </div>

                      <div className="flex items-center justify-between text-[11px] text-slate-500 pt-2 border-t border-slate-100">
                        <span>Created: {fmtDate(pkg.created_at)}</span>
                        {pkg.expiry_date && <span>Expires: {fmtDate(pkg.expiry_date)}</span>}
                      </div>
                    </Card>
                  );
                })}
              </div>
            )}
          </Card>
        </TabsContent>

        {/* --- PAYMENTS & RECEIPTS TAB --- */}
        <TabsContent value="payments">
          <Card className="p-5">
            <div className="flex items-center justify-between gap-3 mb-4">
              <div>
                <h3 className="font-display font-bold text-slate-800 text-base">Receipts & Payment History</h3>
                <p className="text-xs text-slate-500 mt-0.5">
                  All money received, receipt numbers, and payment methods.
                </p>
              </div>
            </div>

            {paymentsList.length === 0 ? (
              <EmptyState icon={Receipt} title="No payments recorded" subtitle="Receipts and collections appear here." />
            ) : (
              <div className="overflow-x-auto border rounded-lg">
                <table className="w-full text-xs text-left">
                  <thead className="bg-slate-50 text-slate-600 uppercase tracking-wider font-semibold border-b">
                    <tr>
                      <th className="py-2.5 px-3">Date</th>
                      <th className="py-2.5 px-3">Receipt / Payment ID</th>
                      <th className="py-2.5 px-3">Method</th>
                      <th className="py-2.5 px-3 text-right">Amount</th>
                      <th className="py-2.5 px-3">Collected By</th>
                      <th className="py-2.5 px-3">Status</th>
                      {user?.role === "admin" && <th className="py-2.5 px-3 text-right">Action</th>}
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-slate-100">
                    {paymentsList.map((pay) => (
                      <tr key={pay.id} className="hover:bg-slate-50/60">
                        <td className="py-2 px-3 text-slate-500 font-mono whitespace-nowrap">
                          {fmtDateTime(pay.date)}
                        </td>
                        <td className="py-2 px-3">
                          <p className="font-bold text-indigo-700 font-mono">{pay.receipt_no || pay.payment_id}</p>
                          {pay.reference && <p className="text-[10px] text-slate-400">Ref: {pay.reference}</p>}
                        </td>
                        <td className="py-2 px-3 uppercase font-medium text-slate-700">
                          {pay.method}
                        </td>
                        <td className="py-2 px-3 text-right tabular-nums font-bold text-slate-900">
                          {inr(pay.amount)}
                        </td>
                        <td className="py-2 px-3 text-slate-500 capitalize">
                          {pay.collected_by_name || pay.collected_by_role}
                        </td>
                        <td className="py-2 px-3">
                          {pay.voided ? (
                            <Badge variant="outline" className="bg-rose-50 text-rose-700 border-rose-200 text-[10px]">
                              Voided
                            </Badge>
                          ) : (
                            <Badge variant="outline" className="bg-emerald-50 text-emerald-700 border-emerald-200 text-[10px]">
                              Valid
                            </Badge>
                          )}
                        </td>
                        {user?.role === "admin" && (
                          <td className="py-2 px-3 text-right">
                            {!pay.voided && (
                              <Button
                                size="sm"
                                variant="outline"
                                onClick={() => {
                                  setSelectedForVoid(pay);
                                  setVoidReason("");
                                  setVoidOpen(true);
                                }}
                                className="h-7 px-2 text-[11px] text-rose-700 border-rose-200 hover:bg-rose-50"
                              >
                                Void
                              </Button>
                            )}
                          </td>
                        )}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </Card>
        </TabsContent>

        <TabsContent value="attendance">
          <Card className="p-5">
            {timeline.filter((e) => e.type === "attendance").length === 0 ? <EmptyState icon={CalendarClock} title="No attendance records yet" /> : (
              <div className="space-y-2">
                {timeline.filter((e) => e.type === "attendance").map((e, i) => (
                  <div key={i} className="flex items-center justify-between py-2 border-b border-slate-100">
                    <span className="text-sm text-slate-600">{fmtDate(e.date)}</span>
                    <StatusBadge status={e.title.split(": ")[1]?.toLowerCase()} source={e.meta} />
                  </div>
                ))}
              </div>
            )}
          </Card>
        </TabsContent>

        <TabsContent value="treatment">
          <div className="flex items-center justify-between mb-4">
            <h3 className="font-display font-bold text-slate-800 text-base">Clinical Treatment Sessions</h3>
            <Button
              size="sm"
              onClick={() => {
                setSelectedTreatment(null);
                setTreatmentRecordOpen(true);
              }}
              className="bg-bk-blue hover:bg-blue-700 text-white gap-1.5 h-8 text-xs"
            >
              <Stethoscope className="h-3.5 w-3.5" /> Add Treatment Session
            </Button>
          </div>

          {painData.length > 1 && (
            <Card className="p-5 mb-4">
              <p className="font-display font-bold text-slate-700 mb-3 flex items-center gap-2">
                <TrendingDown className="h-4 w-4 text-bk-green" /> Pain Reduction Progress
              </p>
              <div className="h-52">
                <ResponsiveContainer width="100%" height="100%">
                  <LineChart data={painData}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#eef2f7" />
                    <XAxis dataKey="n" tick={{ fontSize: 11 }} />
                    <YAxis domain={[0, 10]} tick={{ fontSize: 11 }} width={24} />
                    <Tooltip />
                    <Line type="monotone" dataKey="pain" stroke="#10B981" strokeWidth={2.5} dot={{ r: 4 }} />
                  </LineChart>
                </ResponsiveContainer>
              </div>
            </Card>
          )}

          <Card className="p-5">
            {treatment.length === 0 ? (
              <EmptyState
                icon={Stethoscope}
                title="No treatment sessions recorded yet"
                subtitle="Clinical treatment notes, pain progression, and modalities appear here."
              />
            ) : (
              <div className="space-y-4">
                {treatment.map((t) => {
                  const painBefore = t.pain_score_before ?? t.pain_score;
                  const painAfter = t.pain_score_after ?? t.pain_score;
                  const delta = painBefore != null && painAfter != null ? painBefore - painAfter : null;

                  return (
                    <div
                      key={t.id}
                      className="border border-slate-200 rounded-xl p-4 bg-slate-50/50 hover:bg-white hover:shadow-sm transition-all"
                    >
                      <div className="flex items-start justify-between gap-2">
                        <div>
                          <div className="flex items-center gap-2 flex-wrap">
                            <span className="font-display font-bold text-slate-800 text-sm">
                              Session #{t.session_number}
                            </span>
                            <span className="text-xs text-slate-400">·</span>
                            <span className="text-xs text-slate-600 font-medium">{fmtDate(t.date)}</span>
                            {t.duration && (
                              <Badge variant="outline" className="text-[10px] bg-white text-slate-600">
                                <Clock className="h-2.5 w-2.5 mr-1" />
                                {t.duration} mins
                              </Badge>
                            )}
                          </div>
                          {t.therapist_name && (
                            <p className="text-xs text-slate-400 mt-0.5">Therapist: {t.therapist_name}</p>
                          )}
                        </div>

                        <div className="flex items-center gap-1.5 shrink-0">
                          {painBefore != null && painAfter != null ? (
                            <Badge
                              variant="outline"
                              className="text-xs font-semibold bg-white border-slate-200"
                            >
                              Pain: <span className="text-rose-600">{painBefore}</span> →{" "}
                              <span className="text-emerald-600">{painAfter}</span>
                              {delta > 0 && <span className="text-emerald-700 ml-1 font-bold">(↓{delta})</span>}
                            </Badge>
                          ) : t.pain_score != null ? (
                            <Badge variant="outline" className="text-xs bg-white">
                              Pain: {t.pain_score}/10
                            </Badge>
                          ) : null}
                        </div>
                      </div>

                      {/* Modalities Chips */}
                      {t.modalities?.length > 0 && (
                        <div className="flex flex-wrap gap-1.5 mt-3">
                          {t.modalities.map((m, idx) => (
                            <Badge
                              key={idx}
                              variant="secondary"
                              className="text-[10px] bg-blue-50 text-bk-blue border border-blue-100 font-medium"
                            >
                              {m}
                            </Badge>
                          ))}
                        </div>
                      )}

                      {/* Mobility & Progress Notes */}
                      {t.mobility_note && (
                        <div className="mt-2.5 text-xs text-slate-700 bg-white p-2.5 rounded-md border border-slate-100">
                          <span className="font-semibold text-slate-800">Mobility / Progress: </span>
                          {t.mobility_note}
                        </div>
                      )}

                      {/* Therapist Notes & Clinical Instructions */}
                      {(t.therapist_notes || t.notes || t.next_instruction) && (
                        <div className="mt-2 text-xs text-slate-600 bg-amber-50/50 p-2.5 rounded-md border border-amber-100/60">
                          {t.therapist_notes || t.notes ? (
                            <p>
                              <span className="font-semibold text-amber-900">Notes: </span>
                              {t.therapist_notes || t.notes}
                            </p>
                          ) : null}
                          {t.next_instruction && (
                            <p className="mt-1">
                              <span className="font-semibold text-amber-900">Instruction: </span>
                              {t.next_instruction}
                            </p>
                          )}
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            )}
          </Card>
        </TabsContent>

        <TabsContent value="timeline">
          <Card className="p-5">
            {timeline.length === 0 ? <EmptyState icon={CalendarClock} title="No activity yet" /> : (
              <div className="space-y-3">
                {timeline.map((e, i) => (
                  <div key={i} className="flex gap-3">
                    <div className="flex flex-col items-center">
                      <span className={`h-2.5 w-2.5 rounded-full mt-1.5 ${e.type === "payment" ? "bg-bk-green" : e.type === "attendance" ? "bg-bk-blue" : e.type === "treatment" ? "bg-bk-info" : "bg-slate-300"}`} />
                      {i < timeline.length - 1 && <span className="w-px flex-1 bg-slate-200" />}
                    </div>
                    <div className="pb-3">
                      <p className="text-sm font-medium text-slate-700">{e.title}</p>
                      <p className="text-xs text-slate-400">{fmtDate(e.date)} · {e.meta || ""}</p>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </Card>
        </TabsContent>
      </Tabs>

      {/* --- MODALS --- */}

      {/* Pause Treatment Modal */}
      <Dialog open={pauseOpen} onOpenChange={setPauseOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="font-display flex items-center gap-2 text-amber-700">
              <PauseCircle className="h-5 w-5" /> Pause Treatment
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-3 py-2">
            <div className="space-y-1.5">
              <Label>Pause Until Date <span className="text-red-500">*</span></Label>
              <Input
                type="date"
                value={pauseUntil}
                min={new Date().toISOString().slice(0, 10)}
                onChange={(e) => setPauseUntil(e.target.value)}
              />
              <p className="text-xs text-slate-400">Patient will be excluded from Today&apos;s Clinic until this date.</p>
            </div>
            <div className="space-y-1.5">
              <Label>Reason (Optional)</Label>
              <Input
                placeholder="e.g. Doctor advised rest, Traveling, Fever..."
                value={pauseReason}
                onChange={(e) => setPauseReason(e.target.value)}
              />
            </div>
            <div className="space-y-1.5">
              <Label>Next Visit Date (Optional)</Label>
              <Input
                type="date"
                value={pauseNextVisit}
                onChange={(e) => setPauseNextVisit(e.target.value)}
              />
            </div>
            <div className="space-y-1.5">
              <Label>Notes / Additional Info</Label>
              <Textarea
                rows={2}
                placeholder="Any special remarks..."
                value={pauseNotes}
                onChange={(e) => setPauseNotes(e.target.value)}
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setPauseOpen(false)}>Cancel</Button>
            <Button onClick={handlePause} disabled={actionLoading} className="bg-amber-600 hover:bg-amber-700 text-white">
              {actionLoading ? "Saving..." : "Pause Treatment"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Mark Treatment Completed Modal */}
      <Dialog open={completeOpen} onOpenChange={setCompleteOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="font-display flex items-center gap-2 text-blue-700">
              <CheckCircle2 className="h-5 w-5" /> Mark Treatment Completed
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-3 py-2">
            <div className="space-y-1.5">
              <Label>Completion Date</Label>
              <Input
                type="date"
                value={completeDate}
                onChange={(e) => setCompleteDate(e.target.value)}
              />
            </div>
            <div className="space-y-1.5">
              <Label>Completion Summary / Note</Label>
              <Textarea
                rows={2}
                placeholder="e.g. Full recovery achieved, pain reduced from 8/10 to 0/10..."
                value={completeNote}
                onChange={(e) => setCompleteNote(e.target.value)}
              />
            </div>
            <div className="flex items-center space-x-2 pt-1">
              <Checkbox id="send_wa" checked={completeWa} onCheckedChange={setCompleteWa} />
              <label htmlFor="send_wa" className="text-sm font-medium leading-none cursor-pointer text-slate-700">
                Send treatment completion WhatsApp message
              </label>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setCompleteOpen(false)}>Cancel</Button>
            <Button onClick={handleComplete} disabled={actionLoading} className="bg-blue-600 hover:bg-blue-700 text-white">
              {actionLoading ? "Completing..." : "Complete Treatment"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Discontinue Treatment Modal */}
      <Dialog open={discOpen} onOpenChange={setDiscOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="font-display flex items-center gap-2 text-rose-700">
              <XCircle className="h-5 w-5" /> Discontinue Treatment
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-3 py-2">
            <div className="space-y-1.5">
              <Label>Discontinuation Date</Label>
              <Input
                type="date"
                value={discDate}
                onChange={(e) => setDiscDate(e.target.value)}
              />
            </div>
            <div className="space-y-1.5">
              <Label>Reason</Label>
              <Select value={discReason} onValueChange={setDiscReason}>
                <SelectTrigger><SelectValue placeholder="Select reason" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="relocated">Relocated to another city</SelectItem>
                  <SelectItem value="financial">Financial constraints</SelectItem>
                  <SelectItem value="stopped_responding">Patient stopped responding</SelectItem>
                  <SelectItem value="alternative_treatment">Opted for alternative treatment / surgery</SelectItem>
                  <SelectItem value="dissatisfied">Dissatisfied with progress</SelectItem>
                  <SelectItem value="other">Other</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-1.5">
              <Label>Notes / Details</Label>
              <Textarea
                rows={2}
                placeholder="Additional notes..."
                value={discNote}
                onChange={(e) => setDiscNote(e.target.value)}
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDiscOpen(false)}>Cancel</Button>
            <Button onClick={handleDiscontinue} disabled={actionLoading} className="bg-rose-600 hover:bg-rose-700 text-white">
              {actionLoading ? "Saving..." : "Discontinue Treatment"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Reactivate Treatment Modal */}
      <Dialog open={reactivateOpen} onOpenChange={setReactivateOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="font-display flex items-center gap-2 text-emerald-700">
              <PlayCircle className="h-5 w-5" /> Reactivate Treatment
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-3 py-2">
            <div className="space-y-1.5">
              <Label>Reactivation Date</Label>
              <Input
                type="date"
                value={reactivateDate}
                onChange={(e) => setReactivateDate(e.target.value)}
              />
            </div>
            <div className="space-y-1.5">
              <Label>Schedule</Label>
              <Select value={reactivateSchedule} onValueChange={setReactivateSchedule}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="daily">Daily</SelectItem>
                  <SelectItem value="mon_sat">Mon – Sat</SelectItem>
                  <SelectItem value="mwf">Mon / Wed / Fri</SelectItem>
                  <SelectItem value="tt">Tue / Thu</SelectItem>
                  <SelectItem value="none">No fixed schedule</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-1.5">
              <Label>Next Visit Date (Optional)</Label>
              <Input
                type="date"
                value={reactivateNextVisit}
                onChange={(e) => setReactivateNextVisit(e.target.value)}
              />
            </div>
            <div className="space-y-1.5">
              <Label>Reactivation Notes</Label>
              <Textarea
                rows={2}
                placeholder="Reason for restarting or notes..."
                value={reactivateNote}
                onChange={(e) => setReactivateNote(e.target.value)}
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setReactivateOpen(false)}>Cancel</Button>
            <Button onClick={handleReactivate} disabled={actionLoading} className="bg-emerald-600 hover:bg-emerald-700 text-white">
              {actionLoading ? "Reactivating..." : "Reactivate Treatment"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Refund Modal */}
      <Dialog open={refundOpen} onOpenChange={setRefundOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="font-display flex items-center gap-2 text-rose-700">
              <MinusCircle className="h-5 w-5" /> Process Patient Refund
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-3 py-2">
            <div className="space-y-1.5">
              <Label>Refund Amount (₹) *</Label>
              <Input
                type="number"
                placeholder="e.g. 500"
                value={refundAmount}
                onChange={(e) => setRefundAmount(e.target.value)}
                className="font-bold text-base tabular-nums"
              />
            </div>
            <div className="space-y-1.5">
              <Label>Payment Method</Label>
              <Select value={refundMethod} onValueChange={setRefundMethod}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="cash">Cash</SelectItem>
                  <SelectItem value="upi">UPI</SelectItem>
                  <SelectItem value="bank">Bank Transfer</SelectItem>
                  <SelectItem value="other">Other</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-1.5">
              <Label>Mandatory Reason *</Label>
              <Input
                placeholder="e.g. Patient relocating / treatment stopped"
                value={refundReason}
                onChange={(e) => setRefundReason(e.target.value)}
              />
            </div>
            <div className="space-y-1.5">
              <Label>Additional Notes (Optional)</Label>
              <Textarea
                rows={2}
                placeholder="Internal notes..."
                value={refundNote}
                onChange={(e) => setRefundNote(e.target.value)}
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setRefundOpen(false)}>Cancel</Button>
            <Button
              onClick={handleRefund}
              disabled={refundLoading}
              className="bg-rose-600 hover:bg-rose-700 text-white"
            >
              {refundLoading ? "Processing..." : "Confirm & Debit Ledger"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Manual Adjustment Modal */}
      <Dialog open={adjOpen} onOpenChange={setAdjOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="font-display flex items-center gap-2 text-indigo-700">
              <PlusCircle className="h-5 w-5" /> Manual Ledger Adjustment
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-3 py-2">
            <div className="space-y-1.5">
              <Label>Adjustment Type</Label>
              <Select value={adjDir} onValueChange={setAdjDir}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="credit">Credit (+) Increase Patient Credit</SelectItem>
                  <SelectItem value="debit">Debit (-) Increase Patient Due</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-1.5">
              <Label>Amount (₹) *</Label>
              <Input
                type="number"
                placeholder="e.g. 200"
                value={adjAmount}
                onChange={(e) => setAdjAmount(e.target.value)}
                className="font-bold text-base tabular-nums"
              />
            </div>
            <div className="space-y-1.5">
              <Label>Mandatory Reason *</Label>
              <Input
                placeholder="e.g. Balance correction from historical record"
                value={adjReason}
                onChange={(e) => setAdjReason(e.target.value)}
              />
            </div>
            <div className="space-y-1.5">
              <Label>Notes (Optional)</Label>
              <Textarea
                rows={2}
                placeholder="Notes..."
                value={adjNote}
                onChange={(e) => setAdjNote(e.target.value)}
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setAdjOpen(false)}>Cancel</Button>
            <Button
              onClick={handleManualAdj}
              disabled={adjLoading}
              className="bg-indigo-600 hover:bg-indigo-700 text-white"
            >
              {adjLoading ? "Applying..." : "Apply Adjustment"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Void Payment Modal */}
      <Dialog open={voidOpen} onOpenChange={setVoidOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="font-display flex items-center gap-2 text-rose-700">
              <Ban className="h-5 w-5" /> Void Payment
            </DialogTitle>
          </DialogHeader>
          {selectedForVoid && (
            <div className="space-y-3 py-2">
              <div className="bg-rose-50 border border-rose-200 p-3 rounded-lg text-xs space-y-1">
                <p className="font-semibold text-rose-900">
                  Payment: {selectedForVoid.receipt_no || selectedForVoid.payment_id}
                </p>
                <p className="text-rose-700">
                  Amount: <strong>{inr(selectedForVoid.amount)}</strong> ({selectedForVoid.method?.toUpperCase()})
                </p>
                <p className="text-rose-600 text-[11px]">
                  Voiding will reverse the credit in the patient ledger and mark this payment void.
                </p>
              </div>
              <div className="space-y-1.5">
                <Label>Mandatory Void Reason *</Label>
                <Input
                  placeholder="e.g. Duplicate entry by mistake / bounced cheque"
                  value={voidReason}
                  onChange={(e) => setVoidReason(e.target.value)}
                />
              </div>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setVoidOpen(false)}>Cancel</Button>
            <Button
              onClick={handleVoidPayment}
              disabled={voidLoading}
              className="bg-rose-600 hover:bg-rose-700 text-white"
            >
              {voidLoading ? "Voiding..." : "Confirm Void"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <TreatmentRecordDialog open={treatmentRecordOpen} onOpenChange={setTreatmentRecordOpen} patient={p} onSaved={() => { load(); loadExtras(); }} />
      <PaymentDialog open={payOpen} onOpenChange={setPayOpen} patient={p} service={p.service_type === "home" ? "home" : "clinic"} onSaved={() => { load(); loadExtras(); }} />
      <PatientFormDialog open={editOpen} onOpenChange={setEditOpen} editing={p} onSaved={() => load()} />
    </div>
  );
}

